import { motion } from 'framer-motion';
import { Navbar } from '../components/layout/Navbar';
import { Footer } from '../components/layout/Footer';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, Calendar, Megaphone, Trophy, CheckCircle } from 'lucide-react';

const services = [
  {
    id: 'stall-building',
    icon: Building2,
    title: 'Trade Fair Stall Building',
    shortDesc: 'Custom exhibition booths that captivate and convert',
    fullDesc: 'We design and build stunning exhibition booths that make your brand stand out at trade fairs worldwide. From concept to installation, we handle every detail to ensure your booth becomes a powerful marketing tool.',
    image: 'https://images.unsplash.com/photo-1761195689615-9469b65dac01?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: [
      'Custom 3D Design & Visualization',
      'Premium Quality Materials',
      'Turnkey Installation & Dismantling',
      'On-site Support & Management',
      'Storage & Logistics Solutions',
      'Reusable Modular Systems'
    ],
    benefits: ['Increase brand visibility', 'Generate more leads', 'Create memorable experiences']
  },
  {
    id: 'corporate-events',
    icon: Calendar,
    title: 'Corporate Events',
    shortDesc: 'End-to-end corporate event management',
    fullDesc: 'We conceptualize, plan, and execute corporate events that leave lasting impressions. From intimate board meetings to large-scale conferences, we deliver flawless experiences tailored to your business objectives.',
    image: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: [
      'Conference & Summit Management',
      'Product Launch Events',
      'Award Ceremonies & Galas',
      'Team Building Activities',
      'Venue Selection & Management',
      'Audio-Visual Production'
    ],
    benefits: ['Professional execution', 'Brand enhancement', 'Memorable experiences']
  },
  {
    id: 'live-events',
    icon: Trophy,
    title: 'Live Events',
    shortDesc: 'Spectacular experiences that inspire and unite',
    fullDesc: 'From marathons to corporate galas, we create live events that leave lasting impressions. Our experienced team handles every aspect of event production to deliver flawless experiences.',
    image: 'https://images.unsplash.com/photo-1746046489457-9628dc3b8a1f?crop=entropy&cs=srgb&fm=jpg&q=85',
    features: [
      'Event Production & Direction',
      'Stage Design & Management',
      'Audio-Visual Solutions',
      'Participant Management',
      'Safety & Compliance',
      'Live Broadcasting & Streaming'
    ],
    benefits: ['Brand awareness', 'Community engagement', 'Media coverage']
  },
  {
    id: 'brand-activation',
    icon: Megaphone,
    title: 'Brand Activation',
    shortDesc: 'Strategic experiences that drive engagement',
    fullDesc: 'We create immersive brand experiences that engage consumers, build loyalty, and drive measurable results. Our data-driven approach ensures every activation delivers maximum ROI.',
    image: 'https://images.pexels.com/photos/29708240/pexels-photo-29708240.jpeg',
    features: [
      'Consumer Engagement Campaigns',
      'Product Launches',
      'Experiential Marketing',
      'Pop-up Experiences',
      'Sampling & Demonstrations',
      'ROI Tracking & Analytics'
    ],
    benefits: ['Customer acquisition', 'Brand loyalty', 'Market insights']
  }
];

export default function ServicesPage() {
  return (
    <div className="page-transition" data-testid="services-page">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-navy overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <img
              src="https://images.unsplash.com/photo-1505373877841-8d25f7d46678?auto=format&fit=crop&w=2000&q=80"
              alt="Background"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-3xl"
            >
              <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                Our Services
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl text-white mb-6">
                Comprehensive Event <span className="text-gold">Solutions</span>
              </h1>
              <p className="text-white/80 text-lg md:text-xl leading-relaxed">
                From trade fair stalls to large-scale expos, live events to brand activations - we deliver world-class event experiences tailored to your needs.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services List */}
        <section className="py-20 lg:py-32">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="space-y-24">
              {services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${
                    index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                  }`}
                  data-testid={`service-detail-${service.id}`}
                >
                  {/* Image */}
                  <div className={`${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                    <div className="relative overflow-hidden img-zoom">
                      <img
                        src={service.image}
                        alt={service.title}
                        className="w-full h-[400px] lg:h-[500px] object-cover"
                      />
                      <div className="absolute top-6 left-6">
                        <div className="w-16 h-16 bg-gold flex items-center justify-center">
                          <service.icon className="w-8 h-8 text-navy" />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className={`${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                    <span className="text-gold font-semibold uppercase tracking-wider text-sm mb-4 block">
                      Service 0{index + 1}
                    </span>
                    <h2 className="font-heading text-3xl md:text-4xl text-navy mb-4">
                      {service.title}
                    </h2>
                    <p className="text-muted-foreground text-lg mb-8 leading-relaxed">
                      {service.fullDesc}
                    </p>

                    {/* Features */}
                    <div className="grid sm:grid-cols-2 gap-3 mb-8">
                      {service.features.map((feature) => (
                        <div key={feature} className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                          <span className="text-navy text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* Benefits */}
                    <div className="flex flex-wrap gap-3 mb-8">
                      {service.benefits.map((benefit) => (
                        <span 
                          key={benefit}
                          className="bg-gold/10 text-gold px-4 py-2 text-sm font-medium"
                        >
                          {benefit}
                        </span>
                      ))}
                    </div>

                    <Link
                      to={`/services/${service.id}`}
                      className="btn-primary inline-flex items-center gap-2"
                    >
                      Learn More
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 lg:py-24 bg-muted">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-3xl md:text-4xl text-navy mb-6"
            >
              Ready to Discuss Your Project?
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-muted-foreground text-lg mb-10 max-w-2xl mx-auto"
            >
              Contact us for a free consultation and let's create something extraordinary together.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2">
                Get Free Quote
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
